import { QueryFunctionContext, useQueryClient } from '@tanstack/react-query';
import { map, range } from 'lodash';
import pLimit from 'p-limit';
import { useEffect, useRef, useState } from 'react';
import { UsePaginatedQueriesArgs, usePaginatedQueries } from './usePaginatedQueries';

const DEFAULT_ALL_PAGES_CONCURRENCY = 4;

const normalizePrefetchConcurrency = (concurrency: number) => {
  if (!Number.isFinite(concurrency) || concurrency < 1) {
    return DEFAULT_ALL_PAGES_CONCURRENCY;
  }

  return Math.max(1, Math.floor(concurrency));
};

/**
 * Extends `usePaginatedQueries` with an eager "fetch every page" mode.
 *
 * `enabled` controls whether the all-pages prefetch side effect should run,
 * while `concurrency` caps how many page requests may be in flight at once.
 */
type UseGetAllPaginatedQueriesArgs<
  PageResult = any,
  T = any,
  Args = any,
  Placeholder = PageResult
> = UsePaginatedQueriesArgs<PageResult, T, Args, Placeholder> & {
  enabled?: boolean;
  concurrency?: number;
};

/**
 * Wraps `usePaginatedQueries` and prefetches every page through React Query.
 *
 * This hook is intended for consumers that need a single aggregated view across
 * all paginated results while still reusing the existing `usePaginatedQueries`
 * query state and item aggregation helpers. The prefetch fan-out is limited via
 * `p-limit` so large result sets do not overwhelm the browser or backend.
 */
export const useGetAllPaginatedQueries = <PageResult = any, T = any, Args = any, Placeholder = PageResult>({
  enabled = true,
  concurrency = DEFAULT_ALL_PAGES_CONCURRENCY,
  currentPage,
  getQueryKeyForPage,
  getPageArgs,
  pageQueryFn,
  totalPages,
  ...paginatedQueriesArgs
}: UseGetAllPaginatedQueriesArgs<PageResult, T, Args, Placeholder>) => {
  const queryClient = useQueryClient();
  const getQueryKey = getQueryKeyForPage || getPageArgs;
  const normalizedConcurrency = normalizePrefetchConcurrency(concurrency);
  const prefetchAnchorPages = Array.from(
    new Set([0, currentPage, Math.floor((totalPages - 1) / 2), totalPages - 1].filter((page) => page >= 0))
  );

  // Use a small, representative key sample to decide when to re-evaluate the
  // all-pages prefetch. The effect computes the full-page signature lazily.
  const prefetchAnchorSignature = JSON.stringify(map(prefetchAnchorPages, (pageIndex) => getQueryKey(pageIndex)));

  // Callers often pass inline callbacks. Keep latest function references without
  // requiring caller-side memoization or retriggering the prefetch side-effect.
  const getPageArgsRef = useRef(getPageArgs);
  const getQueryKeyForPageRef = useRef(getQueryKeyForPage);
  const pageQueryFnRef = useRef(pageQueryFn);
  const lastCompletedPrefetchSignatureRef = useRef<string | null>(null);
  const [isPrefetchingAllPages, setIsPrefetchingAllPages] = useState(false);

  getPageArgsRef.current = getPageArgs;
  getQueryKeyForPageRef.current = getQueryKeyForPage;
  pageQueryFnRef.current = pageQueryFn;

  const paginatedQueries = usePaginatedQueries<PageResult, T, Args, Placeholder>({
    currentPage,
    getQueryKeyForPage,
    getPageArgs,
    pageQueryFn,
    totalPages,
    ...paginatedQueriesArgs,
  });

  useEffect(() => {
    if (!enabled || totalPages === 0) {
      setIsPrefetchingAllPages(false);
      return;
    }

    let isCancelled = false;
    const limit = pLimit(normalizedConcurrency);
    const getPageArgsLatest = getPageArgsRef.current;
    const getQueryKeyLatest = getQueryKeyForPageRef.current || getPageArgsLatest;
    const pageQueryFnLatest = pageQueryFnRef.current;
    const pages = range(totalPages);
    const prefetchSignature = JSON.stringify([
      normalizedConcurrency,
      map(pages, (pageIndex) => getQueryKeyLatest(pageIndex)),
    ]);

    if (lastCompletedPrefetchSignatureRef.current === prefetchSignature) {
      setIsPrefetchingAllPages(false);
      return;
    }

    setIsPrefetchingAllPages(true);

    const prefetchPage = async (pageIndex: number) => {
      const pageArgs = getPageArgsLatest(pageIndex);

      // Warm the cache first, then register pages with the paginated hook once
      // the prefetch batch completes so consumers read from cached results.
      await queryClient.prefetchQuery({
        queryKey: getQueryKeyLatest(pageIndex),
        queryFn: (context: QueryFunctionContext) => pageQueryFnLatest(pageArgs, context),
      });
    };

    void Promise.allSettled(map(pages, (pageIndex) => limit(() => prefetchPage(pageIndex)))).then(() => {
      if (isCancelled) {
        return;
      }

      lastCompletedPrefetchSignatureRef.current = prefetchSignature;
      setIsPrefetchingAllPages(false);
      paginatedQueries.fetchPages(pages);
    });

    return () => {
      isCancelled = true;
    };
  }, [enabled, normalizedConcurrency, paginatedQueries.fetchPages, prefetchAnchorSignature, queryClient, totalPages]);

  return {
    ...paginatedQueries,
    isPrefetchingAllPages,
  };
};
